import type { GlobalBetSlipState } from './GlobalBetSlipManager'
import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import { globalBetSlipManager } from './GlobalBetSlipManager'
import GlobalBetSlipOverlay from './GlobalBetSlipOverlay'
import styles from './styles.css?inline'

export interface WidgetConfig {
  league: string
}

export interface WidgetInstance {
  destroy: () => void
  getBetSlip: () => GlobalBetSlipState
  clearBetSlip: () => void
}

/**
 * Container ID for the global overlay element
 * This div is appended to document.body and renders the shared bet slip overlay
 */
const OVERLAY_CONTAINER_ID = 'odds-widget-overlays'

/**
 * Odds Widget SDK Class
 *
 * Manages multiple widget instances and a single shared bet slip overlay.
 * Each widget renders in its own shadow DOM container, and the overlay also uses shadow DOM.
 * All widgets share the same global bet slip state.
 *
 * Architecture:
 * - Widget Instances: Each widget renders independently in its provided container using shadow DOM
 * - Global Overlay: Single bet slip overlay rendered to document.body using shadow DOM
 * - Shared State: All widgets share the same GlobalBetSlipManager instance
 * - Style Isolation: Both widgets and overlay use shadow DOM for complete CSS encapsulation
 *
 * Lifecycle:
 * 1. First widget init: Creates overlay container with shadow DOM and renders overlay
 * 2. Additional widgets: Just create new widget instances with their own shadow DOM
 * 3. Widget destruction: Cleans up individual instances and their bets
 * 4. Last widget destroyed: Removes overlay container from DOM
 */
class OddsWidgetSDK {
  private instances = new Map<string, WidgetInstance>()
  private overlayContainer: HTMLElement | null = null
  private overlayRoot: ReactDOM.Root | null = null

  init(container: string | HTMLElement, config: WidgetConfig): WidgetInstance {
    const containerElement = typeof container === 'string'
      ? document.querySelector(container) as HTMLElement
      : container

    if (!containerElement) {
      throw new Error('Container element not found')
    }

    // Ensure global overlay container exists
    this.ensureOverlayContainer()

    const shadowRoot = containerElement.attachShadow({ mode: 'open' })

    this.injectStylesIntoShadowDOM(shadowRoot)

    const instanceId = this.generateInstanceId()
    const root = ReactDOM.createRoot(shadowRoot)

    const instance: WidgetInstance = {
      destroy: () => this.destroyInstance(instanceId, root),
      getBetSlip: () => globalBetSlipManager.getBetsBySportsbook(),
      clearBetSlip: () => globalBetSlipManager.clearWidget(instanceId),
    }

    const appProps = {
      instanceId,
      league: config.league,
    }

    root.render(React.createElement(App, appProps))

    // Render global overlay (only once for all instances)
    this.renderGlobalOverlay()

    this.instances.set(instanceId, instance)
    return instance
  }

  /**
   * Ensure the global overlay container exists
   * Creates a full-screen div with shadow DOM for the bet slip overlay
   * The container itself has pointer-events: none, but child elements can have pointer events
   */
  private ensureOverlayContainer(): void {
    if (this.overlayContainer) {
      return
    }

    this.overlayContainer = document.getElementById(OVERLAY_CONTAINER_ID)

    if (!this.overlayContainer) {
      this.createNewOverlayContainer()
    }
    else if (!this.overlayRoot) {
      this.recreateOverlayShadowDOM()
    }
  }

  /**
   * Creates a brand new overlay container with shadow DOM setup
   */
  private createNewOverlayContainer(): void {
    this.overlayContainer = document.createElement('div')
    this.overlayContainer.id = OVERLAY_CONTAINER_ID
    this.overlayContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      z-index: 999999; /* High z-index to ensure overlay appears above all other content */
    `
    document.body.appendChild(this.overlayContainer)

    // Create shadow DOM for the overlay
    const shadowRoot = this.overlayContainer.attachShadow({ mode: 'open' })

    // Inject styles
    this.injectStylesIntoShadowDOM(shadowRoot)

    // Create React root directly on shadow DOM
    this.overlayRoot = ReactDOM.createRoot(shadowRoot)
  }

  /**
   * Recreates the shadow DOM setup for an existing overlay container
   * Used when container exists but React root was lost
   */
  private recreateOverlayShadowDOM(): void {
    if (!this.overlayContainer)
      return

    const shadowRoot = this.overlayContainer.shadowRoot
      || this.overlayContainer.attachShadow({ mode: 'open' })

    // Inject styles if not already present
    this.injectStylesIntoShadowDOM(shadowRoot)

    // Create React root directly on shadow DOM
    this.overlayRoot = ReactDOM.createRoot(shadowRoot)
  }

  /**
   * Injects CSS styles into shadow DOM if not already present
   */
  private injectStylesIntoShadowDOM(shadowRoot: ShadowRoot): void {
    if (!shadowRoot.querySelector('style')) {
      // https://github.com/tailwindlabs/tailwindcss/issues/15005#issuecomment-2912976980
      const shadowRootStyles = styles.replace(/\(\(-webkit-hyphens:\s*none\)\)\s*and\s*/g, '')
      const styleElement = document.createElement('style')
      styleElement.textContent = shadowRootStyles
      shadowRoot.appendChild(styleElement)
    }
  }

  /**
   * Render the global bet slip overlay
   * Only renders once when the first widget instance is created
   */
  private renderGlobalOverlay(): void {
    if (!this.overlayRoot) {
      return
    }

    // Render overlay when we have no instances yet (first time only)
    if (this.instances.size === 0) {
      this.overlayRoot.render(
        React.createElement(GlobalBetSlipOverlay),
      )
    }
  }

  private destroyInstance(instanceId: string, root: ReactDOM.Root): void {
    root.unmount()

    // Clear bets from this widget
    globalBetSlipManager.clearWidget(instanceId)

    this.instances.delete(instanceId)

    // Clean up overlay if no instances remain
    if (this.instances.size === 0) {
      this.cleanupOverlayContainer()
    }
  }

  private cleanupOverlayContainer(): void {
    if (this.overlayRoot) {
      this.overlayRoot.unmount()
      this.overlayRoot = null
    }

    if (this.overlayContainer && this.overlayContainer.parentNode) {
      this.overlayContainer.parentNode.removeChild(this.overlayContainer)
      this.overlayContainer = null
    }
  }

  private generateInstanceId(): string {
    return `widget-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  destroyAll(): void {
    // Create a copy to avoid modification during iteration
    const instancesCopy = Array.from(this.instances.values())
    instancesCopy.forEach(instance => instance.destroy())
    // Note: instances.clear() and cleanupOverlayContainer() are already handled
    // by destroyInstance() when the last instance is destroyed
  }
}

// Create and export SDK instance
const sdk = new OddsWidgetSDK()

// Global API for script tag usage
declare global {
  interface Window {
    OddsWidget: typeof sdk
  }
}

if (typeof window !== 'undefined') {
  window.OddsWidget = sdk
}
